import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  MapPin, 
  Plus, 
  Users,
  Phone,
  Mail,
  Clock,
  Building2,
  ArrowLeft,
  Check,
  X,
  AlertCircle,
  User,
  Bell,
  CheckCircle,
  XCircle,
  Edit
} from "lucide-react";

const AdminVenuesPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [venues, setVenues] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [adminNote, setAdminNote] = useState("");
  
  const [createData, setCreateData] = useState({
    name: "",
    location: "",
    capacity: 14,
    pricePerHour: 500,
    format: "7x7",
    hasChangingRoom: true,
    hasShower: true,
    hasParking: true,
    surfaceType: "artificial_grass",
    city: "",
    district: "",
    address: "",
    phone: "",
    description: "",
    availableHours: "08:00-23:00",
    images: [],
    amenities: []
  });

  useEffect(() => {
    if (!user?.isAdmin) {
      toast.error("Bu sayfaya erişim yetkiniz yok");
      navigate("/");
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [venuesRes, claimsRes] = await Promise.all([
        axios.get(`${API}/admin/venues`),
        axios.get(`${API}/admin/venue-claims`)
      ]);
      setVenues(venuesRes.data || []);
      setClaims(claimsRes.data?.claims || []);
    } catch (error) {
      console.error("Veri yüklenemedi:", error);
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVenue = async () => {
    if (!createData.name.trim()) {
      toast.error("Saha adı gerekli");
      return;
    }
    if (!createData.city.trim()) {
      toast.error("Şehir gerekli");
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/venues`, createData);
      
      if (response.data.success) {
        toast.success("Saha oluşturuldu!");
        setShowCreateDialog(false);
        setCreateData({
          name: "",
          location: "",
          capacity: 14,
          pricePerHour: 500,
          format: "7x7",
          hasChangingRoom: true,
          hasShower: true,
          hasParking: true,
          surfaceType: "artificial_grass",
          city: "",
          district: "",
          address: "",
          phone: "",
          description: "",
          availableHours: "08:00-23:00",
          images: [],
          amenities: []
        });
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Saha oluşturulamadı");
    }
  };

  const handleEditClick = (venue) => {
    setEditingVenue(venue);
    setCreateData({
      name: venue.name || "",
      location: venue.location || "",
      capacity: venue.capacity || 14,
      pricePerHour: venue.pricePerHour || 500,
      format: venue.format || "7x7",
      hasChangingRoom: venue.hasChangingRoom ?? true,
      hasShower: venue.hasShower ?? true,
      hasParking: venue.hasParking ?? true,
      surfaceType: venue.surfaceType || "artificial_grass",
      city: venue.city || "",
      district: venue.district || "",
      address: venue.address || "",
      phone: venue.phone || "",
      description: venue.description || "",
      availableHours: venue.availableHours || "08:00-23:00",
      images: venue.images || [],
      amenities: venue.amenities || []
    });
    setShowEditDialog(true);
  };

  const handleUpdateVenue = async () => {
    if (!createData.name.trim()) {
      toast.error("Saha adı gerekli");
      return;
    }

    try {
      const response = await axios.put(`${API}/admin/venues/${editingVenue.id}`, createData);
      
      if (response.data.success) {
        toast.success("Saha güncellendi!");
        setShowEditDialog(false);
        setEditingVenue(null);
        setCreateData({
          name: "",
          location: "",
          capacity: 14,
          pricePerHour: 500,
          format: "7x7",
          hasChangingRoom: true,
          hasShower: true,
          hasParking: true,
          surfaceType: "artificial_grass",
          city: "",
          district: "",
          address: "",
          phone: "",
          description: "",
          availableHours: "08:00-23:00",
          images: [],
          amenities: []
        });
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Saha güncellenemedi");
    }
  };

  const handleClaimAction = async (approved) => {
    if (!selectedClaim) return;
    
    try {
      const response = await axios.post(`${API}/admin/venue-claims/${selectedClaim.venueId}/approve`, {
        claimId: selectedClaim.id,
        approved,
        adminNote
      });
      
      if (response.data.success) {
        toast.success(approved ? "Talep onaylandı!" : "Talep reddedildi");
        setShowClaimDialog(false);
        setSelectedClaim(null);
        setAdminNote("");
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  const getOwnerStatus = (venue) => {
    // "system" veya null/undefined olan ownerId'ler sahipsiz sayılır
    const hasRealOwner = venue.ownerId && venue.ownerId !== "system";
    if (hasRealOwner) {
      return <Badge className="bg-green-500">Sahipli</Badge>;
    } else if (venue.pendingClaimsCount > 0) {
      return <Badge className="bg-yellow-500">{venue.pendingClaimsCount} Talep</Badge>;
    }
    return <Badge className="bg-gray-500">Sahipsiz</Badge>;
  };

  // "system" veya null/undefined olan ownerId'ler sahipsiz sayılır
  const hasRealOwner = (venue) => venue.ownerId && venue.ownerId !== "system";
  
  const filteredVenues = activeTab === "all" 
    ? venues 
    : activeTab === "claimed" 
      ? venues.filter(v => hasRealOwner(v))
      : activeTab === "pending"
        ? venues.filter(v => v.pendingClaimsCount > 0)
        : venues.filter(v => !hasRealOwner(v) && !v.pendingClaimsCount);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg transition-colors duration-300">
      <Navbar user={user} onLogout={onLogout} />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-green-700 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-white hover:bg-white/20"
                onClick={() => navigate("/admin")}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
                  <Building2 className="h-7 w-7" />
                  Saha Yönetimi
                </h1>
                <p className="text-emerald-100 mt-1 text-sm">
                  Halı saha ekle ve talepleri yönet
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {claims.length > 0 && (
                <Button 
                  variant="outline"
                  className="bg-yellow-500 border-yellow-500 text-white hover:bg-yellow-600"
                  onClick={() => setActiveTab("claims")}
                >
                  <Bell className="h-4 w-4 mr-2" />
                  {claims.length} Bekleyen Talep
                </Button>
              )}
              <Button 
                onClick={() => setShowCreateDialog(true)}
                className="bg-white text-emerald-700 hover:bg-emerald-50"
              >
                <Plus className="h-4 w-4 mr-2" />
                Yeni Saha
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Building2 className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{venues.length}</p>
                  <p className="text-xs text-gray-500">Toplam Saha</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Check className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{venues.filter(v => v.ownerId).length}</p>
                  <p className="text-xs text-gray-500">Sahipli</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Bell className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{claims.length}</p>
                  <p className="text-xs text-gray-500">Bekleyen Talep</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{venues.filter(v => !v.ownerId).length}</p>
                  <p className="text-xs text-gray-500">Sahipsiz</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="all">Tümü ({venues.length})</TabsTrigger>
            <TabsTrigger value="claimed">Sahipli</TabsTrigger>
            <TabsTrigger value="pending" className="relative">
              Talepler
              {claims.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-yellow-500 text-white text-xs rounded-full">
                  {claims.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="unclaimed">Sahipsiz</TabsTrigger>
          </TabsList>

          {/* Talepler Tab */}
          <TabsContent value="pending">
            {claims.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <CheckCircle className="h-16 w-16 mx-auto text-green-300 mb-4" />
                  <p className="text-gray-500">Bekleyen talep yok</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {claims.map((claim) => (
                  <Card key={claim.id} className="border-l-4 border-l-yellow-500">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{claim.venueName}</h3>
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {claim.venueCity} {claim.venueDistrict && `/ ${claim.venueDistrict}`}
                          </p>
                          
                          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm font-medium text-gray-700 mb-2">Talep Eden:</p>
                            <div className="space-y-1 text-sm">
                              <p className="flex items-center gap-2">
                                <User className="h-3 w-3 text-gray-400" />
                                {claim.userName}
                              </p>
                              <p className="flex items-center gap-2">
                                <Mail className="h-3 w-3 text-gray-400" />
                                {claim.userEmail}
                              </p>
                              {claim.userPhone && (
                                <p className="flex items-center gap-2">
                                  <Phone className="h-3 w-3 text-gray-400" />
                                  {claim.userPhone}
                                </p>
                              )}
                            </div>
                            {claim.message && (
                              <div className="mt-2 pt-2 border-t">
                                <p className="text-xs text-gray-500">Mesaj:</p>
                                <p className="text-sm">{claim.message}</p>
                              </div>
                            )}
                          </div>
                          
                          <p className="text-xs text-gray-400 mt-2">
                            {new Date(claim.createdAt).toLocaleString('tr-TR')}
                          </p>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button 
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => {
                              setSelectedClaim(claim);
                              setShowClaimDialog(true);
                            }}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Onayla
                          </Button>
                          <Button 
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => {
                              setSelectedClaim(claim);
                              setAdminNote("");
                              handleClaimAction(false);
                            }}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reddet
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Sahalar Tab */}
          <TabsContent value={activeTab !== "pending" ? activeTab : "all"}>
            {filteredVenues.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Building2 className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Saha bulunamadı</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredVenues.map((venue) => (
                  <Card key={venue.id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{venue.name}</CardTitle>
                          <CardDescription className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {venue.city} {venue.district && `/ ${venue.district}`}
                          </CardDescription>
                        </div>
                        {getOwnerStatus(venue)}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span>{venue.format}</span>
                        <span>{venue.capacity} kişi</span>
                        <span className="text-emerald-600 font-medium">{venue.pricePerHour}₺/saat</span>
                      </div>
                      
                      {/* Sahip Bilgisi */}
                      {venue.ownerInfo ? (
                        <div className="bg-green-50 rounded-lg p-3 text-sm">
                          <p className="font-medium text-green-700 mb-1 flex items-center gap-1">
                            <User className="h-3 w-3" />
                            Saha Sahibi
                          </p>
                          <div className="text-green-600">
                            <p>{venue.ownerInfo.name}</p>
                            <p className="text-xs">{venue.ownerInfo.email}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-gray-50 rounded-lg p-3 text-sm">
                          <p className="text-gray-400 italic flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            Sahip atanmamış
                          </p>
                        </div>
                      )}
                      
                      {venue.phone && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Phone className="h-3 w-3" />
                          {venue.phone}
                        </div>
                      )}
                      
                      {/* Düzenle Butonu */}
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full mt-2"
                        onClick={() => handleEditClick(venue)}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Düzenle
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Saha Oluşturma Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-emerald-500" />
              Yeni Saha Ekle
            </DialogTitle>
            <DialogDescription>
              Saha bilgilerini doldurun. Saha sahibi daha sonra kaydolup bu sahayı talep edebilir.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Saha Adı *</Label>
                <Input
                  value={createData.name}
                  onChange={(e) => setCreateData({ ...createData, name: e.target.value })}
                  placeholder="Örn: Yeşil Vadi Halı Saha"
                />
              </div>
              <div className="space-y-2">
                <Label>Format</Label>
                <Select
                  value={createData.format}
                  onValueChange={(value) => setCreateData({ ...createData, format: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5x5">5x5</SelectItem>
                    <SelectItem value="6x6">6x6</SelectItem>
                    <SelectItem value="7x7">7x7</SelectItem>
                    <SelectItem value="8x8">8x8</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Şehir *</Label>
                <Input
                  value={createData.city}
                  onChange={(e) => setCreateData({ ...createData, city: e.target.value })}
                  placeholder="Örn: İstanbul"
                />
              </div>
              <div className="space-y-2">
                <Label>İlçe</Label>
                <Input
                  value={createData.district}
                  onChange={(e) => setCreateData({ ...createData, district: e.target.value })}
                  placeholder="Örn: Kadıköy"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Adres</Label>
              <Input
                value={createData.address}
                onChange={(e) => setCreateData({ ...createData, address: e.target.value })}
                placeholder="Tam adres"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Kapasite</Label>
                <Input
                  type="number"
                  value={createData.capacity}
                  onChange={(e) => setCreateData({ ...createData, capacity: parseInt(e.target.value) || 14 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Saat Ücreti (₺)</Label>
                <Input
                  type="number"
                  value={createData.pricePerHour}
                  onChange={(e) => setCreateData({ ...createData, pricePerHour: parseFloat(e.target.value) || 500 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Çalışma Saatleri</Label>
                <Input
                  value={createData.availableHours}
                  onChange={(e) => setCreateData({ ...createData, availableHours: e.target.value })}
                  placeholder="08:00-23:00"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Telefon</Label>
              <Input
                value={createData.phone}
                onChange={(e) => setCreateData({ ...createData, phone: e.target.value })}
                placeholder="0532 xxx xx xx"
              />
            </div>

            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={createData.hasChangingRoom}
                  onCheckedChange={(checked) => setCreateData({ ...createData, hasChangingRoom: checked })}
                />
                <span className="text-sm">Soyunma Odası</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={createData.hasShower}
                  onCheckedChange={(checked) => setCreateData({ ...createData, hasShower: checked })}
                />
                <span className="text-sm">Duş</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={createData.hasParking}
                  onCheckedChange={(checked) => setCreateData({ ...createData, hasParking: checked })}
                />
                <span className="text-sm">Otopark</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              İptal
            </Button>
            <Button onClick={handleCreateVenue}>
              <Plus className="h-4 w-4 mr-2" />
              Saha Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Talep Onay Dialog */}
      <Dialog open={showClaimDialog} onOpenChange={setShowClaimDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              Talebi Onayla
            </DialogTitle>
            <DialogDescription>
              "{selectedClaim?.venueName}" sahası "{selectedClaim?.userName}" kullanıcısına bağlanacak.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-yellow-50 p-4 rounded-lg text-sm">
              <p className="font-medium text-yellow-800 mb-2">⚠️ Dikkat</p>
              <p className="text-yellow-700">
                Onayladığınızda bu saha ve tüm mevcut rezervasyonları/maçları bu kullanıcıya bağlanacaktır.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Admin Notu (Opsiyonel)</Label>
              <Textarea
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="Onay ile ilgili not ekleyin..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClaimDialog(false)}>
              İptal
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => handleClaimAction(true)}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Onayla ve Bağla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Saha Düzenleme Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-blue-500" />
              Saha Düzenle
            </DialogTitle>
            <DialogDescription>
              {editingVenue?.name} sahasının bilgilerini düzenleyin
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Saha Adı *</Label>
                <Input
                  value={createData.name}
                  onChange={(e) => setCreateData({ ...createData, name: e.target.value })}
                  placeholder="Örn: Yeşil Vadi Halı Saha"
                />
              </div>
              <div className="space-y-2">
                <Label>Format</Label>
                <Select
                  value={createData.format}
                  onValueChange={(value) => setCreateData({ ...createData, format: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5x5">5x5</SelectItem>
                    <SelectItem value="6x6">6x6</SelectItem>
                    <SelectItem value="7x7">7x7</SelectItem>
                    <SelectItem value="8x8">8x8</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Şehir</Label>
                <Input
                  value={createData.city}
                  onChange={(e) => setCreateData({ ...createData, city: e.target.value })}
                  placeholder="Örn: İstanbul"
                />
              </div>
              <div className="space-y-2">
                <Label>İlçe</Label>
                <Input
                  value={createData.district}
                  onChange={(e) => setCreateData({ ...createData, district: e.target.value })}
                  placeholder="Örn: Kadıköy"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Adres</Label>
              <Input
                value={createData.address}
                onChange={(e) => setCreateData({ ...createData, address: e.target.value })}
                placeholder="Tam adres"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Kapasite</Label>
                <Input
                  type="number"
                  value={createData.capacity}
                  onChange={(e) => setCreateData({ ...createData, capacity: parseInt(e.target.value) || 14 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Saat Ücreti (₺)</Label>
                <Input
                  type="number"
                  value={createData.pricePerHour}
                  onChange={(e) => setCreateData({ ...createData, pricePerHour: parseFloat(e.target.value) || 500 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Çalışma Saatleri</Label>
                <Input
                  value={createData.availableHours}
                  onChange={(e) => setCreateData({ ...createData, availableHours: e.target.value })}
                  placeholder="08:00-23:00"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Telefon</Label>
              <Input
                value={createData.phone}
                onChange={(e) => setCreateData({ ...createData, phone: e.target.value })}
                placeholder="0532 xxx xx xx"
              />
            </div>

            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={createData.hasChangingRoom}
                  onCheckedChange={(checked) => setCreateData({ ...createData, hasChangingRoom: checked })}
                />
                <span className="text-sm">Soyunma Odası</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={createData.hasShower}
                  onCheckedChange={(checked) => setCreateData({ ...createData, hasShower: checked })}
                />
                <span className="text-sm">Duş</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={createData.hasParking}
                  onCheckedChange={(checked) => setCreateData({ ...createData, hasParking: checked })}
                />
                <span className="text-sm">Otopark</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              İptal
            </Button>
            <Button onClick={handleUpdateVenue}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminVenuesPage;
